import React, { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ShieldAlert, RefreshCw, ArrowLeft, LogOut, Settings, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CreateAlertCard } from "@/components/CreateAlertCard";
import { AlertsList } from "@/components/AlertsList";
import { AlertTypeSelector } from "@/components/AlertTypeSelector";
import { AnomalyDetectionView } from "@/components/AnomalyDetectionView";
import { QAValidationView } from "@/components/QAValidationView";
import { MasterDashboardView } from "@/components/MasterDashboardView";
import { PercentileAnalysisView } from "@/components/PercentileAnalysisView";
import { WorkloadAnalysisView } from "@/components/WorkloadAnalysisView";
import { AnomalyDetectionV2View } from "@/components/AnomalyDetectionV2View";
import { PercentileAnalysisV2View } from "@/components/PercentileAnalysisV2View";
import { MediaPlannerView } from "@/components/MediaPlannerView";
import { CampaignPerformanceView } from "@/components/CampaignPerformanceView";
import { BudgetUtilizationView } from "@/components/BudgetUtilizationView";
import { CampaignStructureView } from "@/components/CampaignStructureView";
import { PerformanceAlertsView } from "@/components/PerformanceAlertsView";
import { BrandRenderView } from "@/components/BrandRenderView";
import { SafeZonesView } from "@/components/SafeZonesView";
import { SlideValidatorView } from "@/components/SlideValidatorView";
import { QaAdScorerView } from "@/components/QaAdScorerView";
import { QaFullFunnelView } from "@/components/QaFullFunnelView";
import { LoginPage } from "@/components/LoginPage";
import { AuthCallback } from "@/components/AuthCallback";
import { AdminPanel } from "@/components/AdminPanel";
import { NotificationProvider } from "@/context/NotificationContext";
import { NotificationBell } from "@/components/NotificationBell";
import { CombinedFooter } from "@/components/CombinedFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Interceptor global: añade Authorization header desde localStorage en todas las peticiones
// Esto soluciona el problema de CORS cuando el ingress devuelve Access-Control-Allow-Origin: *
axios.interceptors.request.use(config => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Desactivar withCredentials para evitar bloqueos CORS cross-origin con * wildcard
  config.withCredentials = false;
  return config;
});

// Country Sentinel View Component
const CountrySentinelView = ({ onBack, currentUser }) => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Activity tracking (with duplicate prevention)
  const hasTrackedView = useRef(false);
  const trackActivity = useCallback(async (action, details = null) => {
    try {
      await axios.post(`${API}/track-activity`, {
        action,
        module: "country_sentinel",
        details
      }, { withCredentials: true });
    } catch (e) { /* silent */ }
  }, []);

  // Track view on mount (only once)
  useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      trackActivity("view");
    }
  }, [trackActivity]);

  const fetchAlerts = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const response = await axios.get(`${API}/alerts`, { withCredentials: true });
      setAlerts(response.data);
    } catch (error) {
      console.error("Error fetching alerts:", error);
      toast.error("Error al cargar las alertas");
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const handleCreateAlert = async (alertData) => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/alerts`, alertData, { withCredentials: true });
      
      // Track alert creation with full details (using correct field names from CreateAlertCard)
      trackActivity("create", { 
        alert_id: response.data.id || response.data._id,
        databricks_job_id: response.data.databricks_job_id,
        alert_name: response.data.alert_name || alertData.alert_name,
        client: alertData.client_name,
        countries: alertData.authorized_countries,
        schedule_interval: alertData.schedule_interval,
        status: response.data.status
      });
      
      if (response.data.status === "active") {
        if (response.data.notification_status === "configured") {
          toast.success("Alerta creada con notificaciones", {
            description: `La alerta "${response.data.alert_name}" está activa. Las notificaciones por email están configuradas automáticamente.`,
            duration: 6000,
          });
        } else {
          toast.success("Alerta creada exitosamente", {
            description: `La alerta "${response.data.alert_name}" está activa y monitoreando.`,
          });
        }
      } else {
        toast.warning("Alerta creada parcialmente", {
          description: "La alerta se guardó pero hubo un problema con la configuración en Databricks.",
        });
      }
      
      await fetchAlerts();
    } catch (error) {
      console.error("Error creating alert:", error);
      const errorMessage = error.response?.data?.detail || "No se pudo crear la alerta";
      toast.error("Error al crear alerta", {
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      setIsDeleting(true);
      await axios.delete(`${API}/alerts/${alertId}`, { withCredentials: true });
      // Note: Activity tracking is done in the backend
      toast.success("Alerta eliminada correctamente");
      await fetchAlerts();
    } catch (error) {
      console.error("Error deleting alert:", error);
      toast.error("Error al eliminar la alerta");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500">
                  <ShieldAlert className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-800">Country Sentinel</h1>
                  <p className="text-xs text-slate-500">Control de Países</p>
                </div>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchAlerts}
              disabled={isRefreshing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8 space-y-8">
        <div className="space-y-2">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
            Monitoreo de Campañas
          </h2>
          <p className="text-slate-500 text-base max-w-2xl">
            Configura alertas automáticas para detectar cuando las campañas de Google Ads 
            de tus clientes corren en países no autorizados.
          </p>
        </div>

        <CreateAlertCard onCreateAlert={handleCreateAlert} isLoading={isLoading} />
        <AlertsList 
          alerts={alerts} 
          onDeleteAlert={handleDeleteAlert} 
          isDeleting={isDeleting}
          currentUser={currentUser}
        />
      </main>
    </div>
  );
};

// Protected Route Wrapper
const ProtectedRoute = ({ children, user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const checkAttempts = React.useRef(0);

  useEffect(() => {
    // If user is already passed via state from AuthCallback, use it immediately
    if (location.state?.user) {
      setUser(location.state.user);
      setIsChecking(false);
      // Clear the state to avoid stale data
      window.history.replaceState({}, document.title);
      return;
    }

    // If user already exists in React state, skip check
    if (user) {
      setIsChecking(false);
      return;
    }

    // Check authentication via backend (token-based, con fallback a cookie)
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsChecking(false);
      } catch (error) {
        // Si no hay token en localStorage, verificar si hay cookie (sesiones previas)
        if (checkAttempts.current < 2) {
          checkAttempts.current++;
          setTimeout(checkAuth, 200); // Retry after 200ms
          return;
        }
        console.error("Auth check failed after retries:", error);
        // Limpiar token inválido
        localStorage.removeItem("session_token");
        navigate("/login", { replace: true });
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [user, setUser, navigate, location.state]);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return children;
};

// Main App Content (after authentication)
const MainApp = ({ user, setUser, onLogout }) => {
  const [currentView, setCurrentView] = useState("menu");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleSelectType = (type) => {
    if (type === "admin") {
      setCurrentView("admin");
    } else {
      setCurrentView(type);
    }
  };

  const handleBack = () => {
    setCurrentView("menu");
  };

  const handleNavigateToAdmin = () => {
    setCurrentView("admin");
  };

  // Check if current view is admin (footer should not show in admin)
  const showFooter = currentView !== "admin";
  
  // Check if we're in menu view (has sidebar)
  const hasVisibleSidebar = currentView === "menu";

  return (
    <NotificationProvider>
      {/* Global Notification Bell - Fixed position */}
      <div className="fixed top-4 right-4 z-50">
        <NotificationBell onNavigateToAdmin={handleNavigateToAdmin} />
      </div>

      {currentView === "menu" && (
        <AlertTypeSelector 
          onSelectType={handleSelectType} 
          currentUser={user}
          onLogout={onLogout}
          sidebarCollapsed={sidebarCollapsed}
          onSidebarToggle={setSidebarCollapsed}
        />
      )}
      
      {currentView === "admin" && (
        <AdminPanel onBack={handleBack} currentUser={user} />
      )}
      
      {currentView === "master-dashboard" && (
        <MasterDashboardView onBack={handleBack} />
      )}
      
      {currentView === "country-sentinel" && (
        <CountrySentinelView onBack={handleBack} currentUser={user} />
      )}
      
      {currentView === "anomaly-detection" && (
        <AnomalyDetectionView onBack={handleBack} />
      )}
      
      {currentView === "qa-validation" && (
        <QAValidationView onBack={handleBack} />
      )}
      
      {currentView === "percentile-analysis" && (
        <PercentileAnalysisView onBack={handleBack} />
      )}
      
      {currentView === "workload-analysis" && (
        <WorkloadAnalysisView onBack={handleBack} />
      )}

      {currentView === "anomaly-detection-v2" && (
        <AnomalyDetectionV2View onBack={handleBack} />
      )}

      {currentView === "percentile-analysis-v2" && (
        <PercentileAnalysisV2View onBack={handleBack} />
      )}

      {currentView === "media-planner" && (
        <MediaPlannerView onBack={handleBack} />
      )}

      {currentView === "campaign-performance" && (
        <CampaignPerformanceView onBack={handleBack} />
      )}

      {currentView === "budget-utilization" && (
        <BudgetUtilizationView onBack={handleBack} />
      )}

      {currentView === "campaign-structure" && (
        <CampaignStructureView onBack={handleBack} />
      )}

      {currentView === "performance-alerts" && (
        <PerformanceAlertsView onBack={handleBack} />
      )}

      {currentView === "brand-render" && (
        <BrandRenderView onBack={handleBack} />
      )}

      {currentView === "safe-zones" && (
        <SafeZonesView onBack={handleBack} />
      )}

      {currentView === "slide-validator" && (
        <SlideValidatorView onBack={handleBack} />
      )}

      {currentView === "qa-ad-scorer" && (
        <QaAdScorerView onBack={handleBack} />
      )}

      {currentView === "qa-full-funnel" && (
        <QaFullFunnelView onBack={handleBack} />
      )}


      {/* Global Footer - visible in all views except admin */}
      {showFooter && (
        <CombinedFooter 
          isAdmin={user?.role === 'admin'} 
          hasSidebar={hasVisibleSidebar}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}
    </NotificationProvider>
  );
};

// App Router Component
const AppRouter = () => {
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: false });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Limpiar token de localStorage y header de axios
      localStorage.removeItem("session_token");
      delete axios.defaults.headers.common["Authorization"];
      setUser(null);
      navigate("/login", { replace: true });
      toast.success("Sesión cerrada correctamente");
    }
  };

  // Handle auth callback - check URL fragment for session_id DURING RENDER (not in useEffect)
  // This prevents race conditions by processing new session_id FIRST before checking existing session
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onAuthSuccess={(userData) => setUser(userData)} />;
  }

  // Check if dev mode for test route
  const isDevMode = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
  
  return (
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to="/" replace /> : <LoginPage />
      } />
      
      {/* DEV ONLY: Test route to bypass auth for testing */}
      {isDevMode && (
        <Route path="/test-ui" element={
          <MainApp 
            user={{ name: "Test User", email: "test@test.com", role: "admin" }} 
            setUser={setUser} 
            onLogout={() => navigate("/login")} 
          />
        } />
      )}
      
      <Route path="/auth/callback" element={
        <AuthCallback onAuthSuccess={(userData) => setUser(userData)} />
      } />
      
      <Route path="/*" element={
        <ProtectedRoute user={user} setUser={setUser}>
          <MainApp user={user} setUser={setUser} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
    </Routes>
  );
};

// Root App Component
function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
